/**
 * NEXORA AI - Analytics Premium
 * Refactored: Decision-oriented analytics with clear hierarchy
 * Epic 1.3 - Responsive Premium
 * 
 * Principles:
 * - Help user make decisions, not just show data
 * - Answer 3 questions: Health? Best results? Next action?
 * - Above the fold = most important info
 */

import React, { useEffect, useState } from 'react';
import { 
  TrendingUp, TrendingDown, Minus, Users, Share2, MessageCircle, 
  Eye, BarChart3, Award, Zap, ArrowRight, Clock, Plus 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import LazyChart from '../components/charts/LazyChart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

// Static color mapping for Tailwind JIT
const colorVariants = {
  indigo: { bg: 'bg-indigo-600/10', border: 'border-indigo-500/20', text: 'text-indigo-400', icon: 'bg-indigo-500' },
  pink: { bg: 'bg-pink-600/10', border: 'border-pink-500/20', text: 'text-pink-400', icon: 'bg-pink-500' },
  green: { bg: 'bg-green-600/10', border: 'border-green-500/20', text: 'text-green-400', icon: 'bg-green-500' },
  purple: { bg: 'bg-purple-600/10', border: 'border-purple-500/20', text: 'text-purple-400', icon: 'bg-purple-500' },
  blue: { bg: 'bg-blue-600/10', border: 'border-blue-500/20', text: 'text-blue-400', icon: 'bg-blue-500' },
  orange: { bg: 'bg-orange-600/10', border: 'border-orange-500/20', text: 'text-orange-400', icon: 'bg-orange-500' },
};

const Analytics = () => {
  const { t } = useTranslation('analytics');
  const [overview, setOverview] = useState({
    total_posts: 0,
    total_likes: 0,
    total_shares: 0,
    total_comments: 0,
    total_reach: 0,
    platform_stats: {},
    trend: null, // +15.3, -5.2, 0
  });
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [overviewRes, timelineRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/timeline`)
      ]);

      setOverview(overviewRes.data);
      setTimeline(timelineRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error(t('error.loading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Calculate health trend (mock for now, should come from backend)
  const calculateTrend = () => {
    const totalEngagement = overview.total_likes + overview.total_shares + overview.total_comments;
    const avgEngagementPerPost = overview.total_posts > 0 ? totalEngagement / overview.total_posts : 0;
    
    // Mock: simulate trend based on engagement
    if (avgEngagementPerPost > 50) return { value: 15.3, direction: 'up' };
    if (avgEngagementPerPost > 20) return { value: 5.1, direction: 'up' };
    if (avgEngagementPerPost > 10) return { value: -2.4, direction: 'down' };
    return { value: -8.2, direction: 'down' };
  };

  const trend = calculateTrend();

  // Best performing platform
  const bestPlatform = Object.entries(overview.platform_stats).reduce((best, [platform, stats]) => {
    const engagement = stats.likes + stats.shares + stats.comments;
    const engagementRate = stats.posts > 0 ? (engagement / stats.posts) : 0;
    return (!best || engagementRate > best.rate) 
      ? { name: platform, rate: engagementRate, stats } 
      : best;
  }, null);

  // Growing metric (mock - should track vs previous period)
  const growingMetric = { name: t('metrics.reach'), value: 28, period: 'week' };

  // Recommended actions
  const recommendedActions = [
    { 
      icon: Zap, 
      text: t('actions.create_content'), 
      subtitle: t('actions.create_content_subtitle'),
      color: 'blue',
      action: '/ai-generator'
    },
    { 
      icon: Clock, 
      text: t('actions.best_time'), 
      subtitle: t('actions.best_time_subtitle'),
      color: 'purple',
      action: '/publishing'
    },
    { 
      icon: Plus, 
      text: t('actions.connect_platform'), 
      subtitle: t('actions.connect_platform_subtitle'),
      color: 'green',
      action: '/social-accounts'
    },
  ];

  const platformData = Object.entries(overview.platform_stats).map(([name, stats]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    posts: stats.posts,
    engagement: stats.likes + stats.shares + stats.comments
  }));

  const kpiCards = [
    { label: t('kpi.total_posts'), value: overview.total_posts, icon: BarChart3, color: 'indigo' },
    { label: t('kpi.total_reach'), value: formatNumber(overview.total_reach), icon: Users, color: 'blue' },
    { 
      label: t('kpi.engagement_rate'), 
      value: overview.total_posts > 0 
        ? `${((overview.total_likes + overview.total_shares + overview.total_comments) / overview.total_posts).toFixed(1)}%`
        : '0%', 
      icon: Eye, 
      color: 'orange' 
    },
  ];

  if (loading) {
    return (
      <div data-testid="analytics-page" className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="analytics-page" className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8 space-y-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-2">
            {t('title')}
          </h1>
          <p className="text-base sm:text-lg text-slate-600">{t('subtitle')}</p>
        </div>

        {/* SECTION 1: HEALTH HERO CARD */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-6 sm:p-8 text-white shadow-2xl mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-blue-100 text-sm sm:text-base mb-2">{t('health.title')}</p>
              <div className="flex items-center gap-3 mb-3">
                {trend.direction === 'up' ? (
                  <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10" />
                ) : trend.direction === 'down' ? (
                  <TrendingDown className="w-8 h-8 sm:w-10 sm:h-10" />
                ) : (
                  <Minus className="w-8 h-8 sm:w-10 sm:h-10" />
                )}
                <span className="text-4xl sm:text-5xl font-bold">
                  {trend.direction === 'up' ? '+' : ''}{trend.value}%
                </span>
              </div>
              <p className="text-lg sm:text-xl font-medium">
                {trend.direction === 'up' 
                  ? t('health.growing')
                  : trend.direction === 'down' 
                  ? t('health.declining') 
                  : t('health.stable')}
              </p>
              <p className="text-sm text-blue-100 mt-1">{t('health.vs_last_month')}</p>
            </div>
            <div className="w-full sm:w-32 h-32 bg-white/10 rounded-xl backdrop-blur-sm flex items-center justify-center">
              <BarChart3 className="w-16 h-16 text-white/80" />
            </div>
          </div>
        </motion.div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {kpiCards.map((kpi, idx) => {
            const Icon = kpi.icon;
            const colors = colorVariants[kpi.color];
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-12 h-12 ${colors.bg} border ${colors.border} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-1">{kpi.value}</p>
                <p className="text-sm text-slate-600">{kpi.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* SECTION 2: BEST PERFORMANCES */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Award className="w-7 h-7 text-amber-500" />
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('best.title')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Top Post - Mock */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-pink-500" />
                </div>
                <h3 className="font-semibold text-slate-900">{t('best.top_post')}</h3>
              </div>
              <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                {t('best.top_post_example')}
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-pink-600 font-semibold">1.2K {t('metrics.likes')}</span>
                <span className="text-slate-500">LinkedIn</span>
              </div>
            </div>

            {/* Best Platform */}
            {bestPlatform && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{t('best.best_platform')}</h3>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-1 capitalize">{bestPlatform.name}</p>
                <p className="text-sm text-slate-600">
                  {Math.round(bestPlatform.rate)} {t('best.avg_engagement_per_post')}
                </p>
              </div>
            )}

            {/* Growing Metric */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <h3 className="font-semibold text-slate-900">{t('best.growing_metric')}</h3>
              </div>
              <p className="text-2xl font-bold text-slate-900 mb-1">{growingMetric.name}</p>
              <p className="text-sm text-green-600 font-semibold">
                +{growingMetric.value}% {t(`best.this_${growingMetric.period}`)}
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 3: CHARTS */}
        <div className="space-y-8 mb-8">
          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">{t('charts.timeline')}</h2>
            {timeline.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <LazyChart 
                  type="line"
                  data={timeline}
                  config={{
                    xKey: 'date',
                    lines: [
                      { dataKey: 'likes', stroke: '#ec4899', name: t('metrics.likes'), strokeWidth: 2 },
                      { dataKey: 'shares', stroke: '#10b981', name: t('metrics.shares'), strokeWidth: 2 },
                      { dataKey: 'comments', stroke: '#8b5cf6', name: t('metrics.comments'), strokeWidth: 2 },
                    ],
                    showGrid: true,
                    showLegend: true
                  }}
                  height={300}
                />
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">{t('charts.no_data')}</div>
            )}
          </div>

          {/* Platform Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">{t('charts.platform_performance')}</h2>
              {platformData.length > 0 ? (
                <LazyChart 
                  type="bar"
                  data={platformData}
                  config={{
                    xKey: 'name',
                    bars: [
                      { dataKey: 'posts', fill: '#6366f1', name: t('metrics.posts') },
                      { dataKey: 'engagement', fill: '#8b5cf6', name: t('metrics.engagement') }
                    ],
                    showGrid: true,
                    showLegend: true
                  }}
                  height={250}
                />
              ) : (
                <div className="text-center py-12 text-slate-500">{t('charts.no_data')}</div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">{t('charts.distribution')}</h2>
              {platformData.length > 0 ? (
                <LazyChart 
                  type="pie"
                  data={platformData}
                  config={{
                    dataKey: 'posts',
                    nameKey: 'name',
                    colors: COLORS
                  }}
                  height={250}
                />
              ) : (
                <div className="text-center py-12 text-slate-500">{t('charts.no_data')}</div>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 4: RECOMMENDED ACTIONS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-7 h-7 text-yellow-400" />
            <h2 className="text-2xl sm:text-3xl font-bold">{t('actions.title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendedActions.map((action, idx) => {
              const Icon = action.icon;
              const colors = colorVariants[action.color];
              return (
                <a
                  key={idx}
                  href={action.action}
                  className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl p-5 transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white mb-1">{action.text}</p>
                      <p className="text-sm text-slate-300">{action.subtitle}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </a>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;
